<template>
  <div 
    class="fab-container" 
    :class="{ dragging: isDragging }"
    :style="fabPosition"
  >
    <div class="fab-actions">
      <transition-group name="fab-popup" tag="div" class="fab-buttons-wrapper">
        <el-button
          v-for="action in visibleActions"
          :key="action.name"
          class="fab-action-btn"
          :type="action.type"
          circle
          size="large"
          @click="handleAction(action)"
        >
          <el-icon :size="22">
            <component :is="action.icon" />
          </el-icon>
        </el-button>
      </transition-group>
    </div>

    <el-button
      class="fab-main-btn"
      :class="{ 'fab-rotate': isOpen }"
      type="primary"
      circle
      size="large"
      @click="toggleFab"
      @mousedown.prevent="startDrag"
      @touchstart="startDrag"
    >
      <el-icon :size="24">
        <component :is="isOpen ? Close : Service" />
      </el-icon>
    </el-button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { Service, Phone, Refresh, Share, Close, Message } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'

const isOpen = ref(false)
const isDragging = ref(false)
const position = ref({ x: 30, y: 30 })
const startPosition = ref({ x: 0, y: 0 })
const fabOffset = ref({ x: 0, y: 0 })
const hasMoved = ref(false)

const fabPosition = computed(() => ({
  right: `${position.value.x}px`,
  bottom: `${position.value.y}px`
}))

const visibleActions = computed(() => isOpen.value ? actions : [])

const actions = [
  { name: 'refresh', type: 'info', icon: Refresh, label: 'Refresh Page' },
  { name: 'phone', type: 'success', icon: Phone, label: 'Dial Service', value: '18690320261' },
  { name: 'email', type: 'primary', icon: Message, label: 'Email Support', value: 'support@yolal.cn' },
  { name: 'share', type: 'warning', icon: Share, label: 'Share' }
]

const toggleFab = () => {
  if (isDragging.value) return
  isOpen.value = !isOpen.value
}

const startDrag = (e) => {
  if (e.type === 'mousedown' && e.button !== 0) return
  isDragging.value = true
  hasMoved.value = false

  const clientX = e.touches ? e.touches[0].clientX : e.clientX
  const clientY = e.touches ? e.touches[0].clientY : e.clientY

  startPosition.value = { x: clientX, y: clientY }
  fabOffset.value = { ...position.value }

  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', stopDrag)
  document.addEventListener('touchmove', onDrag, { passive: false })
  document.addEventListener('touchend', stopDrag)
}

const onDrag = (e) => {
  if (!isDragging.value) return
  e.preventDefault()

  const clientX = e.touches ? e.touches[0].clientX : e.clientX
  const clientY = e.touches ? e.touches[0].clientY : e.clientY

  const deltaX = clientX - startPosition.value.x
  const deltaY = clientY - startPosition.value.y

  if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) hasMoved.value = true

  const newX = fabOffset.value.x - deltaX
  const newY = fabOffset.value.y - deltaY

  const maxX = window.innerWidth - 70
  const maxY = window.innerHeight - (isOpen.value ? 260 : 70) - 10
  const minY = 10

  position.value.x = Math.max(10, Math.min(maxX, newX))
  position.value.y = Math.max(minY, Math.min(maxY, newY))
}

const stopDrag = () => {
  if (!isDragging.value) return
  isDragging.value = false

  if (hasMoved.value) {
    localStorage.setItem('fab-position', JSON.stringify(position.value))
  }

  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
  document.removeEventListener('touchmove', onDrag)
  document.removeEventListener('touchend', stopDrag)
}

const handleAction = (action) => {
  switch (action.name) {
    case 'phone': window.location.href = `tel:${action.value}`; break
    case 'email': window.location.href = `mailto:${action.value}`; break
    case 'refresh': window.location.reload(); break
    case 'share':
      if (navigator.share) {
        navigator.share({ title: document.title, url: window.location.href })
      }
      break
  }
  toggleFab()
}

onMounted(() => {
  const saved = localStorage.getItem('fab-position')
  if (saved) {
    try { position.value = JSON.parse(saved) } catch (e) {}
  }
})

onUnmounted(() => {
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
})
</script>

<style scoped>
.fab-container {
  position: fixed;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  width: 70px;
  user-select: none;
}

.fab-container.dragging {
  opacity: 0.9;
}

.fab-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  min-height: 0;
}

.fab-buttons-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.fab-action-btn {
  width: 50px !important;
  height: 50px !important;
  border-radius: 50% !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  border: none !important;
  margin: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.fab-main-btn {
  width: 60px !important;
  height: 60px !important;
  border-radius: 50% !important;
  box-shadow: 0 6px 16px rgba(0,0,0,0.2);
  border: none !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.fab-rotate {
  transform: rotate(45deg);
  transition: transform 0.3s ease;
}

.fab-popup-enter-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.fab-popup-leave-active {
  transition: all 0.2s ease;
}
.fab-popup-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
.fab-popup-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>